import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ADMIN_PROSPECT_CAMPAIGNS_PAGE_SIZE,
  buildListAdminProspectCampaignsRequest,
  listAdminProspectCampaigns,
} from '../../services/admin/prospectCampaignsService.js';
import { formatAdminDate } from './adminUserDisplay.js';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectCampaignBuildStatusBadgeClass,
  adminProspectCampaignBuildStatusLabel,
  adminProspectCampaignErrorKey,
  classifyAdminCallableError,
} from './adminProspectCampaignDisplay.js';

export default function AdminProspectCampaignsPage() {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [campaigns, setCampaigns] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const requestSeqRef = useRef(0);

  const loadList = async (pageToken, { paging = false, nextStack } = {}) => {
    const requestId = ++requestSeqRef.current;
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    try {
      const data = await listAdminProspectCampaigns(
        buildListAdminProspectCampaignsRequest({
          pageSize: ADMIN_PROSPECT_CAMPAIGNS_PAGE_SIZE,
          pageToken: pageToken || null,
        }),
      );
      if (requestId !== requestSeqRef.current) return;
      const rows = Array.isArray(data?.campaigns) ? data.campaigns : [];
      setCampaigns(rows);
      setNextPageToken(data?.nextPageToken || null);
      setCurrentToken(pageToken || null);
      if (Array.isArray(nextStack)) setTokenStack(nextStack);
      else if (!paging) setTokenStack([]);
      setStatus(rows.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      if (requestId !== requestSeqRef.current) return;
      setCampaigns([]);
      setNextPageToken(null);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
    }
  };

  useEffect(() => {
    loadList(undefined);
  }, []);

  const handleNext = () => {
    if (!nextPageToken) return;
    loadList(nextPageToken, { paging: true, nextStack: [...tokenStack, currentToken] });
  };

  const handlePrevious = () => {
    if (tokenStack.length === 0) return;
    const stack = [...tokenStack];
    const prev = stack.pop();
    loadList(prev, { paging: true, nextStack: stack });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-start gap-2">
        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('admin.prospection.campaign.list.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.campaign.list.subtitle')}</p>
        </div>
      </div>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.campaign.list.loading')}
        </p>
      ) : null}
      {status === 'paging' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.campaign.list.paging')}
        </p>
      ) : null}
      {status === 'error' ? (
        <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
          {t(adminProspectCampaignErrorKey(errorKind))}
        </p>
      ) : null}
      {status === 'empty' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <p>{t('admin.prospection.campaign.list.empty')}</p>
          <Link
            to="/admin/prospection"
            className="mt-3 inline-flex rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.campaign.list.emptyCta')}
          </Link>
        </div>
      ) : null}

      {campaigns.length > 0 && (status === 'ready' || status === 'paging') ? (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.field.name')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.field.buildStatus')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.field.targetCount')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.field.selectedCount')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.field.createdAt')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.field.snapshotCompletedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <Link
                        to={`/admin/prospection/campaigns/${item.id}`}
                        className="font-medium text-indigo-700 hover:underline"
                      >
                        {item.name || ADMIN_DATE_PLACEHOLDER}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {t('admin.prospection.campaign.field.status')}: {item.status || ADMIN_DATE_PLACEHOLDER}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${adminProspectCampaignBuildStatusBadgeClass(item.buildStatus)}`}
                      >
                        {adminProspectCampaignBuildStatusLabel(item.buildStatus, t)}
                      </span>
                    </td>
                    <td className="px-3 py-2">{item.targetCount ?? ADMIN_DATE_PLACEHOLDER}</td>
                    <td className="px-3 py-2">{item.selectedCountExpected ?? ADMIN_DATE_PLACEHOLDER}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(item.createdAt, language)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatAdminDate(item.snapshotCompletedAt, language)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="space-y-3 md:hidden">
            {campaigns.map((item) => (
              <li key={item.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <Link
                  to={`/admin/prospection/campaigns/${item.id}`}
                  className="text-base font-semibold text-indigo-700 hover:underline"
                >
                  {item.name || ADMIN_DATE_PLACEHOLDER}
                </Link>
                <p className="mt-2">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${adminProspectCampaignBuildStatusBadgeClass(item.buildStatus)}`}
                  >
                    {adminProspectCampaignBuildStatusLabel(item.buildStatus, t)}
                  </span>
                </p>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-600">
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.prospection.campaign.field.targetCount')}</dt>
                    <dd>{item.targetCount ?? ADMIN_DATE_PLACEHOLDER}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.prospection.campaign.field.selectedCount')}</dt>
                    <dd>{item.selectedCountExpected ?? ADMIN_DATE_PLACEHOLDER}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.prospection.campaign.field.createdAt')}</dt>
                    <dd>{formatAdminDate(item.createdAt, language)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">
                      {t('admin.prospection.campaign.field.snapshotCompletedAt')}
                    </dt>
                    <dd>{formatAdminDate(item.snapshotCompletedAt, language)}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {(status === 'ready' || status === 'paging' || status === 'empty') &&
      (tokenStack.length > 0 || nextPageToken) ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={tokenStack.length === 0 || status === 'paging'}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {t('admin.prospection.campaign.list.prev')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!nextPageToken || status === 'paging'}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {t('admin.prospection.campaign.list.next')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
